import React from "react";
import PropTypes from "prop-types";
import TableRow from "@mui/material/TableRow";
import { Checkbox, Collapse, TableCell } from "@mui/material";
import TableStyle from "styles/application/TableStyle";
import ModelTaskExpand from "./ModelTaskExpand";
import ModelTaskRowCellContent from "./ModelTaskRowCellContent";
import ModelTaskRowActions from "./ModelTaskRowActions";
import ColourConstants from "helpers/colourConstants";

const AT = TableStyle();
const EMPTY_TASK = {};
// Brief tint after a bulk action so the affected rows are findable in a long list.
const RECENTLY_CHANGED_BACKGROUND = "#e3f0fc";

// The actual row markup, called from ModelTaskRow's useMemo so this stays
// memoised against that hook's own (narrower than "all props") dependency
// list - see the comment there before changing how this is invoked.
const ModelTaskRowContent = ({
	rowIndex,
	shouldHandleClick,
	onHandleTableExpand,
	toggle,
	isRecentlyChanged,
	rows,
	row,
	showSelection,
	isRowSelected,
	onSelectedChange,
	columns,
	classes,
	cx,
	toolTipColumn,
	disableLink,
	onTooltipOpen,
	onNameClick,
	access,
	index,
	selectedData,
	anchorEl,
	data,
	state,
	customCaptions,
	onDotMenuClick,
	onClickAway,
	onDuplicate,
	onCopy,
	onCopyTaskQuestion,
	onSwitchToServiceLayout,
	onRemove,
	singleTask,
	setSingleTask,
	originalRow,
}) => (
	<>
		<TableRow
			aria-rowindex={rowIndex}
			onClick={(e) => {
				if (!shouldHandleClick.current) {
					shouldHandleClick.current = true;
					return;
				} else onHandleTableExpand(!toggle, rows?.id, e);
			}}
			style={{
				background: toggle
					? ColourConstants.tableRowExpand
					: isRecentlyChanged
					? RECENTLY_CHANGED_BACKGROUND
					: ColourConstants.tableRowNormal,
				transition: "background 600ms ease",
				borderBottom: "hidden",
				color: toggle ? ColourConstants.tableRowNormal : "",
				cursor: "pointer",
			}}
			id={`taskExpandable${rows.id}`}
		>
			{showSelection && (
				<TableCell
					padding="checkbox"
					className={cx(classes.dataCell, classes.selectionCell, {
						[classes.rowDivider]: !toggle,
					})}
					onClick={(event) => {
						// stopPropagation alone is enough to keep this click from
						// reaching the row's own onClick (which toggles expansion).
						// Also setting shouldHandleClick here left it poisoned for
						// whatever click came next, silently swallowing it - that
						// was the "checkbox swallows the next expand click" bug.
						event.stopPropagation();
					}}
				>
					<Checkbox
						size="small"
						checked={isRowSelected}
						onChange={(event) => onSelectedChange(rows.id, event.target.checked)}
						inputProps={{ "aria-label": `Select ${rows.name}` }}
					/>
				</TableCell>
			)}
			{columns.map((col, i, arr) => (
				<TableCell
					key={col}
					className={cx(classes.dataCell, { [classes.rowDivider]: !toggle })}
					style={{
						padding: "0 10px",
						maxWidth: "220px",
						color: toggle ? ColourConstants.tableRowNormal : "",
						height: "30px",
					}}
					id={`dataCell${col}`}
				>
					<AT.CellContainer key={col}>
						<ModelTaskRowCellContent
							col={col}
							value={rows[col]}
							isTooltipColumn={toolTipColumn.includes(col)}
							toggle={toggle}
							disableLink={disableLink}
							onTooltipOpen={() => onTooltipOpen(col)}
							onNameClick={onNameClick}
						/>

						{arr.length === i + 1 && (
							<ModelTaskRowActions
								access={access}
								toggle={toggle}
								index={index}
								selectedData={selectedData}
								anchorEl={anchorEl}
								isLast={index === data.length - 1 || index === data.length - 2}
								taskId={rows.id}
								isPublished={state?.modelDetail?.isPublished}
								disableLink={disableLink}
								customCaptions={customCaptions}
								onDotMenuClick={onDotMenuClick}
								onClickAway={onClickAway}
								onEdit={(tg) => onHandleTableExpand(tg, rows?.id)}
								onDuplicate={onDuplicate}
								onCopy={onCopy}
								onCopyTaskQuestion={onCopyTaskQuestion}
								onSwitchToServiceLayout={onSwitchToServiceLayout}
								onRemove={onRemove}
							/>
						)}
					</AT.CellContainer>
				</TableCell>
			))}
		</TableRow>
		<TableRow id={`taskExpanded${row.id}`}>
			<TableCell
				className={cx({ [classes.rowDivider]: toggle })}
				style={{ paddingBottom: 0, paddingTop: 0, background: ColourConstants.tableRowExpand }}
				colSpan={18}
			>
				{/* Not animated. The virtualiser measures this row with a
				    ResizeObserver, and a height transition reports a new size on
				    every frame - measured at 32 callbacks in 381ms for one row -
				    which the browser cannot deliver in a single frame. */}
				<Collapse in={toggle} timeout={0} unmountOnExit>
					<ModelTaskExpand
						state={state}
						customCaptions={customCaptions}
						taskInfo={singleTask ?? EMPTY_TASK}
						setTaskInfo={setSingleTask}
						taskLoading={!singleTask?.id}
						access={access}
						originalRow={originalRow}
					/>
				</Collapse>
			</TableCell>
		</TableRow>
	</>
);

ModelTaskRowContent.propTypes = {
	rowIndex: PropTypes.number,
	shouldHandleClick: PropTypes.object.isRequired,
	onHandleTableExpand: PropTypes.func.isRequired,
	toggle: PropTypes.bool.isRequired,
	isRecentlyChanged: PropTypes.bool,
	rows: PropTypes.object.isRequired,
	row: PropTypes.object.isRequired,
	showSelection: PropTypes.bool,
	isRowSelected: PropTypes.bool,
	onSelectedChange: PropTypes.func,
	columns: PropTypes.arrayOf(PropTypes.string).isRequired,
	classes: PropTypes.object.isRequired,
	cx: PropTypes.func.isRequired,
	toolTipColumn: PropTypes.arrayOf(PropTypes.string).isRequired,
	disableLink: PropTypes.bool,
	onTooltipOpen: PropTypes.func.isRequired,
	onNameClick: PropTypes.func.isRequired,
	access: PropTypes.string,
	index: PropTypes.number.isRequired,
	selectedData: PropTypes.number,
	anchorEl: PropTypes.object,
	data: PropTypes.array.isRequired,
	state: PropTypes.object,
	customCaptions: PropTypes.object,
	onDotMenuClick: PropTypes.func.isRequired,
	onClickAway: PropTypes.func.isRequired,
	onDuplicate: PropTypes.func.isRequired,
	onCopy: PropTypes.func.isRequired,
	onCopyTaskQuestion: PropTypes.func.isRequired,
	onSwitchToServiceLayout: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	singleTask: PropTypes.object,
	setSingleTask: PropTypes.func.isRequired,
	originalRow: PropTypes.object,
};

export default ModelTaskRowContent;
